// Profit First — Firm cash tab.
// Monday allocator + CAP vs actual + a 13-week Opex+Vault cash runway (with
// Profit as a break-glass failsafe) + the quarterly true-up. Balances come
// live from QuickBooks; income and outflows from the revenue engine + P&L;
// CAPs, schedule and the deposit log from firm settings.

import { useState, useEffect, useMemo, useCallback } from 'react';
import SectionLabel from '../../components/common/SectionLabel';
import { useFinancialData } from '../../context/FinancialDataContext';
import { subscribeFirmSettings } from '../../utils/firmSettingsStore';
import { useQbAccounts } from './useQbAccounts';
import { computeRunway, bufferFromOutflows } from './runway';
import { loadProfitFirstSettings, saveProfitFirstSettings, logDeposit } from './profitFirstSettings';
import RunwayChart from './RunwayChart';

const MK = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const money = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(Math.round(Number(n) || 0));
const CARD = 'bg-white border border-line rounded-[4px] shadow-[0_2px_6px_rgba(2,10,40,0.07)]';

// Monday of the current week (runway start).
function thisMonday() {
  const d = new Date(); d.setHours(0, 0, 0, 0);
  const dow = d.getDay();
  d.setDate(d.getDate() + (dow === 0 ? -6 : 1 - dow));
  return d;
}

export default function ProfitFirst() {
  const { global2026Monthly, annualSummary } = useFinancialData();
  const qb = useQbAccounts();
  const [settings, setSettings] = useState(loadProfitFirstSettings);
  useEffect(() => subscribeFirmSettings(() => setSettings(loadProfitFirstSettings())), []);
  const update = useCallback((next) => { setSettings(next); saveProfitFirstSettings(next); }, []);
  const [dep, setDep] = useState('14,120');

  // Monthly income + steady outflow averages from the reconciled P&L.
  const derived = useMemo(() => {
    const months = global2026Monthly?.months || {};
    const monthlyIncome = {};
    let sumPay = 0, sumCon = 0, sumRef = 0, sumCard = 0, cnt = 0;
    MK.forEach((mk, i) => {
      const m = months[mk]; if (!m) return;
      monthlyIncome[i] = Object.values(m.income || {}).reduce((s, v) => s + (Number(v) || 0), 0);
      const cogs = m.cogs || {}, exp = m.expenses || {};
      const contractors = Number(exp.payroll_non_employee) || 0;
      const expTotal = Object.values(exp).reduce((s, v) => s + (Number(v) || 0), 0);
      sumPay += (Number(cogs.payroll_wages_direct) || 0) + (Number(cogs.payroll_taxes_direct) || 0);
      sumCon += contractors;
      sumRef += Number(cogs.referral_fees) || 0;
      sumCard += expTotal - contractors; // everything except contractors rides the card
      cnt += 1;
    });
    const n = cnt || 1;
    return { monthlyIncome, avgPayroll: sumPay / n, avgContractors: sumCon / n, avgReferral: sumRef / n, avgCard: sumCard / n };
  }, [global2026Monthly]);

  const model = useMemo(() => {
    const ownerMonthly = (Number(settings.ownerAnnualSalary) || 0) / 12;
    const outflows = {
      employeePayrollPerRun: Math.max(0, derived.avgPayroll - ownerMonthly) / 2,
      contractorPerWeek: derived.avgContractors / 4.33,
      referralMonthly: derived.avgReferral,
      cardMonthly: derived.avgCard,
    };
    const buffer = settings.bufferOverride != null && settings.bufferOverride !== ''
      ? Number(settings.bufferOverride) : bufferFromOutflows(outflows);
    const balances = qb.buckets || { opex: 0, vault: 0, profit: 0 };
    const runway = computeRunway({
      start: thisMonday(), weeks: 13, monthlyIncome: derived.monthlyIncome,
      opexCapPct: (Number(settings.caps.opex) || 65) / 100, outflows, schedule: settings.schedule, balances, buffer,
    });
    return { outflows, buffer, runway, balances };
  }, [derived, settings, qb.buckets]);

  const caps = settings.caps;
  const depNum = Number(String(dep).replace(/[^0-9.]/g, '')) || 0;
  const splits = {
    profit: depNum * (caps.profit / 100), owner: depNum * (caps.owner / 100),
    tax: depNum * (caps.tax / 100), opex: depNum * (caps.opex / 100),
  };
  const revenue = Number(annualSummary?.projected_revenue_2026) || 0;
  const netIncome = Number(annualSummary?.projected_net_income_2026) || 0;
  const realProfitPct = revenue ? (netIncome / revenue) * 100 : null;

  const { runway, buffer } = model;
  const setCap = (k, v) => update({ ...settings, caps: { ...caps, [k]: Number(v) || 0 } });

  return (
    <div className="max-w-[1040px]">
      <div className="mb-4">
        <SectionLabel>Firm &middot; Profit First</SectionLabel>
        <h1 className="text-[24px] font-black text-navy leading-tight">Profit First</h1>
        <p className="text-[13px] text-ink-2 mt-1">Allocate every Monday, watch the Opex + Vault runway, sweep what is truly above buffer.</p>
      </div>

      {qb.error ? (
        <div className={`${CARD} p-4 mb-5 border-l-[3px] border-l-rose`}>
          <div className="text-[13px] font-bold text-rose">Could not load QuickBooks balances</div>
          <div className="text-[12px] text-ink-2 mt-1">{qb.error}</div>
          <button onClick={qb.reload} className="mt-3 px-3 py-1.5 text-[12px] font-bold rounded-[4px] bg-navy text-white">Retry</button>
        </div>
      ) : qb.loading && !qb.buckets ? (
        <div className={`${CARD} p-6 mb-5 text-[13px] text-ink-2`}>Loading live balances from QuickBooks&hellip;</div>
      ) : (
        <>
          {/* summary tiles */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
            <Tile k="Safe to sweep to Profit" v={money(runway.safeToSweep)} note="above buffer at the 13-week low" accent="deep-teal" big={runway.safeToSweep > 0} />
            <Tile k="13-week cash low point" v={money(runway.low.pool)} note={runway.low.date ? `on ${MONTHS[runway.low.date.getMonth()]} ${runway.low.date.getDate()}` : ''} accent="rose" warn={runway.low.pool < buffer} />
            <Tile k="Buffer floor" v={money(buffer)} note="1 month of salary + expenses" />
            <Tile k="Profit failsafe" v={money(model.balances.profit)} note="break-glass; half is your quarterly draw" />
          </div>

          {/* accounts */}
          <div className={`${CARD} mb-5`}>
            <div className="flex items-center justify-between px-4 pt-3 pb-2 border-b border-line-2">
              <SectionLabel>Accounts</SectionLabel>
              <button onClick={qb.reload} className="text-[11px] font-bold text-bullogic-blue">{qb.loading ? 'Refreshing…' : 'Refresh'}</button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 divide-x divide-line-2">
              <Acct name="Income" v={model.balances.income} />
              <Acct name="Opex" v={model.balances.opex} pool />
              <Acct name="Vault" v={model.balances.vault} pool />
              <Acct name="Owner's Comp" v={model.balances.owner} />
              <Acct name="Tax" v={model.balances.tax} />
              <Acct name="Profit" v={model.balances.profit} />
              <Acct name="Credit cards" v={-(qb.cardTotal || 0)} owed />
            </div>
            {qb.asOf && <div className="px-4 py-2 text-[10.5px] text-ink-3 border-t border-line-2">Live from QuickBooks &middot; {new Date(qb.asOf).toLocaleString('en-US')}</div>}
          </div>

          {/* runway */}
          <div className={`${CARD} mb-5`}>
            <div className="px-4 pt-3 pb-1"><SectionLabel>13-week cash runway</SectionLabel>
              <p className="text-[12px] text-ink-2 -mt-2 mb-2">Opex + Vault against employee payroll, contractors, referral ACH and the card. Owner&rsquo;s pay and taxes draw from their own accounts.</p>
            </div>
            <div className="px-3">
              <RunwayChart daily={runway.daily} low={runway.low} buffer={buffer} failsafe={model.balances.profit} />
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-[10px] uppercase tracking-[0.04em] text-ink-2 font-black bg-[#faf9f6]">
                    <th className="text-left px-3 py-2">Week of</th><th className="text-right px-3 py-2">To Opex</th>
                    <th className="text-right px-3 py-2">Payroll</th><th className="text-right px-3 py-2">Contractors</th>
                    <th className="text-right px-3 py-2">Referral</th><th className="text-right px-3 py-2">Card</th>
                    <th className="text-right px-3 py-2">Ending pool</th>
                  </tr>
                </thead>
                <tbody>
                  {runway.weeks.map((w, i) => {
                    const under = w.end < buffer;
                    const cell = (v) => (v ? money(v) : <span className="text-ink-3">&middot;</span>);
                    return (
                      <tr key={i} className={`text-[12.5px] tabular-nums border-b border-line-2 ${under ? 'bg-rose/5' : ''}`}>
                        <td className="text-left px-3 py-1.5 text-ink-2">{MONTHS[w.date.getMonth()]} {w.date.getDate()}</td>
                        <td className="text-right px-3 py-1.5">{cell(w.in)}</td>
                        <td className="text-right px-3 py-1.5">{cell(w.pay)}</td>
                        <td className="text-right px-3 py-1.5">{cell(w.con)}</td>
                        <td className="text-right px-3 py-1.5">{cell(w.ref)}</td>
                        <td className="text-right px-3 py-1.5">{cell(w.card)}</td>
                        <td className={`text-right px-3 py-1.5 font-bold ${under ? 'text-rose' : 'text-navy'}`}>{money(w.end)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* quarterly true-up */}
          <div className={`${CARD} p-4 mb-5`}>
            <SectionLabel>Quarterly true-up</SectionLabel>
            <table className="w-full text-[12.5px] tabular-nums">
              <tbody>
                <Row label="Opex + Vault at 13-week low" v={money(runway.low.pool)} />
                <Row label="Less buffer floor (1 mo. outflows)" v={money(buffer)} />
                <Row label="Safe to sweep to Profit now" v={money(runway.safeToSweep)} strong accent="deep-teal" />
                <Row label="Profit balance after sweep" v={money(model.balances.profit + runway.safeToSweep)} />
                <Row label="Then take half as your distribution" v={money((model.balances.profit + runway.safeToSweep) / 2)} strong accent="navy" />
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* monday allocator + CAP/ACT + settings — no QB balances needed */}
      <div className="grid md:grid-cols-2 gap-5">
        <div className={`${CARD} p-4`}>
          <SectionLabel>Monday allocator</SectionLabel>
          <label className="text-[11px] font-bold uppercase tracking-[0.03em] text-ink-2">This week&rsquo;s income deposit</label>
          <div className="flex items-center gap-1.5 mt-2 mb-3">
            <span className="text-[22px] font-black text-ink-3">$</span>
            <input value={dep} onChange={(e) => setDep(e.target.value)} className="flex-1 border border-line rounded-[4px] bg-[#fbfaf7] px-3 py-2 text-[22px] font-black tabular-nums text-navy focus:outline-none focus:border-bullogic-blue" />
          </div>
          {[['Profit', 'profit', 'deep-teal'], ["Owner's Comp", 'owner', 'deep-teal'], ['Tax', 'tax', 'amber'], ['Opex', 'opex', 'bullogic-blue']].map(([label, k]) => (
            <div key={k} className="flex items-center gap-3 py-1.5">
              <span className="text-[12.5px] font-bold text-near-black w-[110px]">{label}</span>
              <span className="text-[11px] text-ink-2 w-[34px] tabular-nums">{caps[k]}%</span>
              <span className="ml-auto text-[15px] font-black text-navy tabular-nums">{money(splits[k])}</span>
            </div>
          ))}
          <button onClick={() => update(logDeposit(settings, depNum, splits))} className="mt-3 w-full px-3 py-2 text-[12px] font-bold rounded-[4px] bg-navy text-white">Log this week&rsquo;s allocation</button>
          {settings.depositLog?.length > 0 && (
            <div className="mt-3 text-[11px] text-ink-2">Last logged: {settings.depositLog[0].date} &middot; {money(settings.depositLog[0].amount)} ({settings.depositLog.length} on file)</div>
          )}
        </div>

        <div className={`${CARD} p-4`}>
          <SectionLabel>CAP vs actual &amp; settings</SectionLabel>
          <table className="w-full text-[12.5px] mb-3">
            <thead>
              <tr className="text-[10px] uppercase tracking-[0.04em] text-ink-2 font-black">
                <th className="text-left py-1">Bucket</th><th className="text-right py-1">CAP %</th><th className="text-right py-1">Actual</th>
              </tr>
            </thead>
            <tbody>
              {[['Profit', 'profit'], ["Owner's Comp", 'owner'], ['Tax', 'tax'], ['Opex', 'opex']].map(([label, k]) => (
                <tr key={k} className="border-b border-line-2">
                  <td className="text-left py-1.5 text-near-black">{label}</td>
                  <td className="text-right py-1.5">
                    <input value={caps[k]} onChange={(e) => setCap(k, e.target.value)} className="w-[52px] text-right border border-line rounded-[3px] bg-[#fbfaf7] px-1.5 py-0.5 tabular-nums text-navy font-bold focus:outline-none focus:border-bullogic-blue" />
                  </td>
                  <td className="text-right py-1.5 tabular-nums text-ink-2">
                    {k === 'profit' && realProfitPct != null ? `${realProfitPct.toFixed(1)}%` : <span className="text-ink-3">from Mondays</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {realProfitPct != null && (
            <p className="text-[12px] text-ink-2 mb-3">You allocate <b className="text-navy">{caps.profit}%</b> to Profit but the P&amp;L shows a <b className="text-deep-teal">{realProfitPct.toFixed(1)}%</b> real margin. The extra pools in Opex and Owner&rsquo;s Comp; the runway shows what is safe to move.</p>
          )}
          <div className="grid grid-cols-2 gap-3">
            <label className="text-[11px] font-bold uppercase tracking-[0.03em] text-ink-2">Owner salary / yr
              <input value={settings.ownerAnnualSalary} onChange={(e) => update({ ...settings, ownerAnnualSalary: Number(String(e.target.value).replace(/[^0-9.]/g, '')) || 0 })} className="mt-1 w-full border border-line rounded-[3px] bg-[#fbfaf7] px-2 py-1 text-[13px] tabular-nums text-navy font-bold focus:outline-none focus:border-bullogic-blue" />
            </label>
            <label className="text-[11px] font-bold uppercase tracking-[0.03em] text-ink-2">Buffer override
              <input value={settings.bufferOverride ?? ''} placeholder={`auto ${money(bufferFromOutflows(model.outflows))}`} onChange={(e) => update({ ...settings, bufferOverride: e.target.value === '' ? null : Number(String(e.target.value).replace(/[^0-9.]/g, '')) || 0 })} className="mt-1 w-full border border-line rounded-[3px] bg-[#fbfaf7] px-2 py-1 text-[13px] tabular-nums text-navy font-bold focus:outline-none focus:border-bullogic-blue" />
            </label>
          </div>
          <p className="text-[11px] text-ink-3 mt-3">Schedule: payroll on the 15th &amp; last business day, contractors Fridays, referral ACH the 20th, card in full on the 7th.</p>
        </div>
      </div>
    </div>
  );
}

function Tile({ k, v, note, accent, warn, big }) {
  const top = accent === 'deep-teal' ? 'border-t-deep-teal' : accent === 'rose' ? 'border-t-rose' : 'border-t-navy';
  const vc = warn ? 'text-rose' : big ? 'text-deep-teal' : 'text-navy';
  return (
    <div className={`${CARD} p-3.5 border-t-[3px] ${top}`}>
      <div className="text-[10.5px] font-black uppercase tracking-[0.04em] text-ink-2">{k}</div>
      <div className={`text-[23px] font-black tabular-nums mt-1 ${vc}`}>{v}</div>
      {note && <div className="text-[11px] text-ink-2 mt-0.5">{note}</div>}
    </div>
  );
}

function Acct({ name, v, pool, owed }) {
  return (
    <div className={`px-3 py-2.5 ${pool ? 'bg-bullogic-blue/[0.06] shadow-[inset_3px_0_0_var(--color-bullogic-blue)]' : ''}`}>
      <div className="text-[10px] font-bold uppercase tracking-[0.03em] text-ink-2 truncate">{name}</div>
      <div className={`text-[15px] font-black tabular-nums mt-1 ${owed ? 'text-rose' : 'text-navy'}`}>{money(v)}</div>
      <div className={`text-[9px] font-bold uppercase tracking-[0.03em] ${pool ? 'text-bullogic-blue' : owed ? 'text-rose' : 'text-ink-3'}`}>{pool ? 'buffer pool' : owed ? 'owed' : ' '}</div>
    </div>
  );
}

function Row({ label, v, strong, accent }) {
  const c = accent === 'deep-teal' ? 'text-deep-teal' : accent === 'navy' ? 'text-navy' : 'text-near-black';
  return (
    <tr className="border-b border-line-2 last:border-0">
      <td className={`text-left py-1.5 ${strong ? `font-black ${c}` : 'text-ink-2'}`}>{label}</td>
      <td className={`text-right py-1.5 tabular-nums ${strong ? `font-black ${c}` : 'text-navy'}`}>{v}</td>
    </tr>
  );
}
